import { IconHelp, IconLogout, IconSearch, IconSettings, Popover } from "@dream/ui";
import type { ReactNode } from "react";

/**
 * Menu ⋯ da frame View options (62:24517).
 *
 * Atalhos iguais ao kit: Configurações (G then S), paleta (⌘K), ajuda, sair.
 */

export interface OverflowMenuProps {
  onSettings: () => void;
  onCommandPalette: () => void;
  onHelp: () => void;
  onLogout: () => void;
  trigger?: ReactNode;
}

export function OverflowMenu({
  onSettings,
  onCommandPalette,
  onHelp,
  onLogout,
  trigger,
}: OverflowMenuProps) {
  return (
    <Popover
      align="end"
      side="bottom"
      width="min(17.5rem, calc(100vw - 1.5rem))"
      trigger={
        trigger ?? (
          <button
            type="button"
            aria-label="Opções de visualização"
            data-testid="view-options"
            className="dream-icon-btn size-9"
          >
            <span className="text-[1.15rem] leading-none">⋯</span>
          </button>
        )
      }
    >
      <div className="p-1.5" data-testid="view-options-menu">
        <MenuRow
          icon={<IconSettings className="size-4" />}
          label="Configurações"
          hint="G then S"
          onClick={onSettings}
        />
        <MenuRow
          icon={<IconSearch className="size-4" />}
          label="Abrir linha de comando"
          hint="⌘K"
          onClick={onCommandPalette}
        />
        <MenuRow icon={<IconHelp className="size-4" />} label="Ajuda" onClick={onHelp} />
        <div className="my-1 h-px bg-[var(--color-line)]" />
        <MenuRow
          icon={<IconLogout className="size-4" />}
          label="Sair"
          tone="danger"
          onClick={onLogout}
        />
      </div>
    </Popover>
  );
}

function MenuRow({
  icon,
  label,
  hint,
  onClick,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] font-medium",
        "transition-[background-color,color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
        tone === "danger"
          ? "text-[#AB3434] hover:bg-[#FEEDED]"
          : "text-[var(--color-ink)] hover:bg-[var(--color-surface-subtle)]",
      ].join(" ")}
    >
      <span className={tone === "danger" ? "text-[#AB3434]" : "text-[var(--color-ink-muted)]"}>
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint !== undefined && (
        <span className="shrink-0 text-[11px] font-semibold text-[var(--color-ink-muted)]">
          {hint}
        </span>
      )}
    </button>
  );
}
